import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useQueryClient } from '@tanstack/react-query';
import { useDocPrefsStore } from '../../stores/docPrefsStore';
import { useUserPrefsStore } from '../../stores/userPrefsStore';
import { clearSandboxSession } from '../../hooks/useSandboxSession';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AuthContext = React.createContext();

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const queryClient = useQueryClient();

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      fetchUser();
    } else {
      setLoading(false);
    }
  }, []);

  const fetchUser = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
    } catch (error) {
      // /auth/me failed — token is invalid or backend unreachable.
      // Drop the token so subsequent requests don't carry a stale header
      // and force the user back through the login flow.
      console.error('Auth /me failed; clearing token:', error?.response?.status || error?.message);
      localStorage.removeItem('token');
      delete axios.defaults.headers.common['Authorization'];
    }
    setLoading(false);
  };

  const login = async (email, password) => {
    try {
      // ---- Step 1: hit /auth/login with a clean header set. We do NOT
      // wipe the previous session's keys yet — if the login attempt
      // fails (wrong password, network error) the user stays signed
      // in to whatever they had before. Login itself doesn't need an
      // auth header so a brief delete is safe; we restore it on error.
      const prevHeader = axios.defaults.headers.common['Authorization'];
      delete axios.defaults.headers.common['Authorization'];
      let response;
      try {
        response = await axios.post(`${API}/auth/login`, { email, password });
      } catch (err) {
        // Restore the prior header so the previous session keeps working.
        if (prevHeader) axios.defaults.headers.common['Authorization'] = prevHeader;
        throw err;
      }

      const { access_token } = response.data;

      // ---- Step 2: now that we KNOW the login succeeded, atomically
      // purge prior session keys and inject the new token. Doing this
      // here (after the network call) means there's no window during
      // which in-flight requests can race with a half-swapped header.
      try {
        localStorage.removeItem('sandbox_meta');
        localStorage.removeItem('sandbox_lead_captured');
        sessionStorage.removeItem('sandbox-banner-dismissed');
      } catch { /* storage may be unavailable in Safari private mode */ }
      useDocPrefsStore.getState().reset();
      useUserPrefsStore.getState().reset();
      queryClient.clear();
      clearSandboxSession();

      localStorage.setItem('token', access_token);
      axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;

      // Fetch user and set state directly
      const userResponse = await axios.get(`${API}/auth/me`);
      setUser(userResponse.data);

      return { success: true };
    } catch (error) {
      return { success: false, error: error.response?.data?.detail || 'Login failed' };
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    delete axios.defaults.headers.common['Authorization'];
    setUser(null);
    // Clear cached per-user stores + TanStack Query cache so next login starts clean
    useDocPrefsStore.getState().reset();
    useUserPrefsStore.getState().reset();
    queryClient.clear();
    clearSandboxSession();
  };

  return (
    <AuthContext.Provider value={{ user, login, logout, loading, fetchUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = React.useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
